import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  useSidebar,
} from "@/components/ui/sidebar";
import { useProfile } from "@/context/ProfileContext";
import { useProductScope } from "@/context/ProductScopeContext";
import { useIntelligenceAccess } from "@/hooks/use-intelligence-access";
import { useSalesInsightThreadsAll } from "@/hooks/queries/use-sales-insight-threads";
import { useThreadActions } from "@/hooks/use-thread-actions";

const THREADS_PER_PAGE = 10;

type ThreadSummary = NonNullable<
  ReturnType<typeof useSalesInsightThreadsAll>["data"]
>[number];

function ThreadRow({
  thread,
  businessId,
  isCurrent,
  onNavigate,
}: {
  thread: ThreadSummary;
  businessId: string;
  isCurrent: boolean;
  onNavigate: () => void;
}) {
  const { t } = useTranslation("nav");
  const navigate = useNavigate();
  const { renameThread, deleteThread } = useThreadActions(businessId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(thread.title ?? "");

  const title = thread.title?.trim() || t("chats.untitled");

  const startEdit = () => {
    setDraft(thread.title ?? "");
    setEditing(true);
  };

  const commitEdit = async () => {
    await renameThread(thread.id, draft, thread.title);
    setEditing(false);
  };

  const handleDelete = async () => {
    await deleteThread(thread.id, {
      topicKey: thread.topicKey,
      scope: thread.scope,
    });
    if (isCurrent) navigate("/coach");
  };

  if (editing) {
    return (
      <SidebarMenuItem>
        <div className="mx-1 flex items-center gap-1 rounded-md border bg-background px-2 py-1">
          <input
            autoFocus
            aria-label={t("chats.rename")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void commitEdit();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setEditing(false);
              }
            }}
            onBlur={() => void commitEdit()}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </div>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        size="sm"
        isActive={isCurrent}
        onClick={onNavigate}
      >
        <Link to={`/coach?thread=${thread.id}`} title={title}>
          <span className="truncate">{title}</span>
        </Link>
      </SidebarMenuButton>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction showOnHover>
            <MoreHorizontal className="size-4" aria-hidden="true" />
            <span className="sr-only">{t("chats.more")}</span>
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" className="w-40">
          <DropdownMenuItem onClick={startEdit}>
            <Pencil className="size-3.5" aria-hidden="true" />
            {t("chats.rename")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => void handleDelete()}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
            {t("chats.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

/**
 * Recent coach chats in the sidebar (restored from the pre-redesign nav): a
 * collapsible group listing the business's threads newest first, ten at a
 * time, with rename/delete in a row menu. Hidden while the sidebar is
 * collapsed to icons, for businesses without Intelligence, and outside the
 * Coach workspace (GRA-78) — the threads are Coach chats, so surfacing them
 * while Recruit or Leads is the active product just reads as noise.
 */
export function NavChats() {
  const { t } = useTranslation("nav");
  const location = useLocation();
  const { setOpenMobile } = useSidebar();
  const { businessProfile } = useProfile();
  const businessId = businessProfile?.uid ?? "";
  const entitled = useIntelligenceAccess();
  const { activeProduct } = useProductScope();
  const shouldLoad = entitled && activeProduct === "coach";

  const threadsQuery = useSalesInsightThreadsAll(
    shouldLoad ? businessId : undefined,
  );
  const threads = threadsQuery.data ?? [];
  const [visibleCount, setVisibleCount] = useState(THREADS_PER_PAGE);

  const currentThreadId =
    location.pathname === "/coach"
      ? new URLSearchParams(location.search).get("thread")
      : null;

  const handleNavigate = () => setOpenMobile(false);

  if (!entitled || activeProduct !== "coach") return null;

  return (
    <Collapsible
      defaultOpen
      className="group/chats group-data-[collapsible=icon]:hidden"
    >
      <SidebarGroup>
        <CollapsibleTrigger asChild>
          <SidebarGroupLabel className="cursor-pointer select-none hover:text-sidebar-foreground">
            {t("chats.title")}
            <ChevronRight
              aria-hidden="true"
              className="ml-auto size-3.5 transition-transform duration-150 group-data-[state=open]/chats:rotate-90"
            />
          </SidebarGroupLabel>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenu className="gap-0">
            {threadsQuery.isLoading ? (
              <>
                <SidebarMenuSkeleton showIcon={false} />
                <SidebarMenuSkeleton showIcon={false} />
                <SidebarMenuSkeleton showIcon={false} />
              </>
            ) : threads.length === 0 ? (
              <SidebarMenuItem>
                <SidebarMenuButton asChild size="sm">
                  <Link to="/coach" onClick={handleNavigate}>
                    <span className="truncate text-muted-foreground">
                      {t("chats.start")}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ) : (
              <>
                {threads.slice(0, visibleCount).map((thread) => (
                  <ThreadRow
                    key={thread.id}
                    thread={thread}
                    businessId={businessId}
                    isCurrent={thread.id === currentThreadId}
                    onNavigate={handleNavigate}
                  />
                ))}
                {threads.length > visibleCount ? (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      size="sm"
                      onClick={() =>
                        setVisibleCount((c) => c + THREADS_PER_PAGE)
                      }
                      className="text-muted-foreground"
                    >
                      {t("chats.showMore")}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ) : null}
              </>
            )}
          </SidebarMenu>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}
